import React, {Component} from 'react';
import {RouteComponentProps} from 'react-router';
import {logPageView} from 'services/analytics';
import LandlordDashboardLayout, {DashboardTab} from "layouts/LandlordDashboard";
import DateRangePicker from "components/DateRangePicker";
import './index.scss';
import {DateRange, DateTime, TimeSpan} from "util/time";
import {loadSettings} from "services/pricing";
import {BookingSettings} from "services/entities/booking";
import {handleError} from "util/errors";
import RevenueChart from "pages/landlords/components/RevenueChart";
import RevenueStats from "pages/landlords/components/RevenueStats";
import {Button} from "@material-ui/core";
import {downloadLandlordBookingsStats} from "services/bookings/landlords";
import {downloadFile} from "util/file";
import ProgressSpinner from "components/ProgressSpinner";

interface LandlordDashboardPageState {
  dateRange: DateRange|null;
  bookingSettings: BookingSettings|null;
  maxDate: DateTime|null;
  downloadingReport: boolean;
}

export default class LandlordDashboardPage extends Component<RouteComponentProps, LandlordDashboardPageState> {
  static readonly MAX_RANGE_DURATION = new TimeSpan({years: 1});
  static readonly MAX_RANGE_OFFSET = new TimeSpan({months: 3});
  static readonly DEFAULT_START_OFFSET = new TimeSpan({weeks: 5});
  static readonly DEFAULT_END_OFFSET = new TimeSpan({weeks: 5});

  state: LandlordDashboardPageState = {
    dateRange: null,
    bookingSettings: null,
    maxDate: null,
    downloadingReport: false
  };

  componentDidMount(): void {
    logPageView(this.props.location.pathname, 'Landlord Dashboard');
    loadSettings().then(
      bookingSettings => this.setSettings(bookingSettings),
      ex => {
        handleError("Failed loading settings", ex);
        this.setSettings(null);
      }
    );
  }

  render() {
    const downloadingReport = this.state.downloadingReport;
    return <LandlordDashboardLayout id="landlord-dashboard-page" currentTab={DashboardTab.Overview}>
      <section className="title">
        <h1>Landlord Dashboard</h1>
      </section>
      <section className="dates">
        <Button className={`download-report ${downloadingReport ? ' loading': ''}`} onClick={this.onDownloadReportClick} disabled={downloadingReport}>
          { downloadingReport && <ProgressSpinner /> }
          Download report
        </Button>
        <DateRangePicker
          value={this.state.dateRange}
          timeZone={this.state.bookingSettings?.defaultTimeZone}
          maxDate={this.state.maxDate || undefined}
          maxDuration={LandlordDashboardPage.MAX_RANGE_DURATION}
          onChange={this.onDateRangeChange}/>
        <RevenueChart dateRange={this.state.dateRange} />
      </section>
      <RevenueStats dateRange={this.state.dateRange} />
    </LandlordDashboardLayout>;
  }

  setSettings(settings: BookingSettings|null) {
    const timezone = settings?.defaultTimeZone;
    const today = DateTime.today(timezone);
    const currentWeek = today.startOf('week');
    const dateRange = {
      start: currentWeek.subtract(LandlordDashboardPage.DEFAULT_START_OFFSET),
      end: currentWeek.add(LandlordDashboardPage.DEFAULT_END_OFFSET)};
    const maxDate = today.add(LandlordDashboardPage.MAX_RANGE_OFFSET);
    this.setState({dateRange: dateRange, maxDate: maxDate, bookingSettings: settings});
  }

  onDownloadReportClick = () => {
    if(!this.state.dateRange) {
      return;
    }
    this.setState({downloadingReport: true});
    downloadLandlordBookingsStats(this.state.dateRange).then(
      file => {
        this.setState({downloadingReport: false});
        downloadFile(URL.createObjectURL(file), 'landlord-report.csv');
      },
      ex => {
        this.setState({downloadingReport: false});
        handleError('Failed to download report', ex);
      }
    );
  };

  onDateRangeChange = (range: DateRange|null) => {
    if(range) {
      this.setState({dateRange: range});
    }
  };
}
